'use client';

import { FC, useRef } from 'react';
import { Content } from '@prismicio/client';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import Slider from 'react-slick';
import {
  ChevronLeft,
  ChevronRight,
  ArrowUp,
  ArrowDown,
  Star,
  ArrowUpRight,
} from 'lucide-react';
import ArrowDownRightIcon from '@/assets/ArrowDownRightIcon';
import ArrowUpRightIcon from '@/assets/ArrowUpRightIcon';

import WithFilters from './variations/WithFilters';
import { TestimonialCarousel2SliceWithFiltersPrimaryCardsItem } from '../../../prismicio-types';
import slickStyles from '@/styles/slick.module.css';

export type CaseStudiesCarouselProps =
  SliceComponentProps<Content.TestimonialCarousel2Slice>;

const CaseStudiesCarousel: FC<CaseStudiesCarouselProps> = ({ slice }) => {
  const sliderRef = useRef<Slider>(null);

  const settings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    arrows: false,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
        },
      },
      {
        breakpoint: 640,
        settings: {
          slidesToShow: 1,
        },
      },
    ],
  };

  const isWithoutBgp = slice.variation === 'caseStudiesCarouselWithoutBgp';
  const isLogoAndReview = slice.variation === 'casestudieswithlogoandreview';

  const getBackgroundClass = () => {
    if (slice.variation === 'caseStudiesCarouselWithoutBgp') {
      return 'bg-gradient-to-b from-[#0F423A] to-[#0E1010]';
    }
    if (slice.variation === 'casestudieswithlogoandreview') {
      return 'bg-gradient-to-b from-[#0F423A] to-[#0E1110]';
    }
    if (slice.variation === 'default') {
      return 'bg-primary-shade-green-darker';
    }
    return '';
  };

  if (slice.variation === 'withFilters') {
    return (
      <WithFilters
        slice={slice}
        caseStudies={
          slice.primary
            .cards as TestimonialCarousel2SliceWithFiltersPrimaryCardsItem[]
        }
        loadMoreText={slice.primary.loadmorecasesbuttontext}
        clearFiltersText={slice.primary.clearfilterbuttontext}
      />
    );
  }

  const { title, description, primary_action, cards } = slice.primary;

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={`relative text-white py-20 ${getBackgroundClass()}`}
    >
      <div className="container mx-auto px-4">
        {isLogoAndReview ? (
          // Header for Logo and Review variation
          <div className="text-center mb-16">
            {slice.primary.clutch_logo && (
              <div className="flex justify-center items-center mb-6">
                <PrismicNextImage
                  field={slice.primary.clutch_logo}
                  className="h-8 object-contain mr-4"
                  fallbackAlt=""
                />
                <div className="flex items-center">
                  <span className="text-2xl font-bold mr-2">5.0</span>
                  <div className="flex">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className="w-5 h-5 fill-yellow-400 text-yellow-400"
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}
            <div className="text-4xl md:text-5xl font-bold mb-4">
              <PrismicRichText field={title} />
            </div>
            <div className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
              <PrismicRichText field={description} />
            </div>
            <PrismicNextLink
              field={primary_action}
              className="inline-flex items-center px-8 py-4 font-semibold transition-colors duration-300"
              style={{
                borderRadius: '100px',
                borderTop: '1px solid #EAEAEA',
                background: 'rgba(190, 190, 190, 0.30)',
                color: 'white',
              }}
            >
              {primary_action?.text || 'See What Clients Say About Us'}
            </PrismicNextLink>
          </div>
        ) : (
          // Original header for other variations
          <div className="flex justify-between items-center mb-8">
            <div className="max-w-2xl">
              <div className="text-4xl font-bold mb-4">
                <PrismicRichText field={title} />
              </div>
              <div className="text-lg">
                <PrismicRichText field={description} />
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <PrismicNextLink
                field={primary_action}
                className="secondary-button"
              >
                View case studies
              </PrismicNextLink>
              <div className="flex space-x-2">
                <button
                  onClick={() => sliderRef.current?.slickPrev()}
                  className="p-2 rounded-full border border-white/20 hover:bg-white/10 transition-colors"
                >
                  <ChevronLeft size={24} />
                </button>
                <button
                  onClick={() => sliderRef.current?.slickNext()}
                  className="p-2 rounded-full border border-white/20 hover:bg-white/10 transition-colors"
                >
                  <ChevronRight size={24} />
                </button>
              </div>
            </div>
          </div>
        )}

        <Slider ref={sliderRef} {...settings} className={slickStyles.slider}>
          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          {cards.map((item: any, index) => {
            if (isLogoAndReview) {
              const logoReviewItem =
                item as Content.TestimonialCarousel2SliceCasestudieswithlogoandreviewPrimaryCardsItem;
              const rating = logoReviewItem.stars || 5;
              const fullStars = Math.floor(rating);
              const hasHalfStar = rating % 1 !== 0;

              return (
                <div key={index} className="px-3">
                  <div
                    className="rounded-2xl p-6 flex flex-col justify-between text-left h-full"
                    style={{
                      background: 'rgba(255, 255, 255, 0.10)',
                      boxShadow: '0 4.325px 64.871px 0 rgba(0, 0, 0, 0.08)',
                      backdropFilter: 'blur(10px)',
                    }}
                  >
                    {/* Company logo and arrow */}
                    <div className="flex justify-between items-start mb-4">
                      <PrismicNextImage
                        field={logoReviewItem.card_image}
                        className="w-16 h-12 object-contain flex-shrink-0"
                        fallbackAlt=""
                      />
                      <ArrowUpRight className="w-6 h-6 text-[#05F36C]" />
                    </div>

                    {/* Headline */}
                    <h3
                      className="mb-4"
                      style={{
                        color: '#FFF',
                        fontFamily: 'Inter',
                        fontSize: '18px',
                        fontWeight: 600,
                      }}
                    >
                      {logoReviewItem.card_headline}
                    </h3>

                    {/* Star rating with decimal support */}
                    <div className="flex items-center mb-4">
                      {[...Array(5)].map((_, i) => {
                        if (i < fullStars) {
                          return (
                            <Star
                              key={i}
                              className="w-4 h-4 fill-yellow-400 text-yellow-400"
                            />
                          );
                        } else if (i === fullStars && hasHalfStar) {
                          return (
                            <div key={i} className="relative w-4 h-4">
                              <Star className="w-4 h-4 text-gray-400 absolute" />
                              <div className="overflow-hidden w-1/2">
                                <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                              </div>
                            </div>
                          );
                        } else {
                          return (
                            <Star key={i} className="w-4 h-4 text-gray-400" />
                          );
                        }
                      })}
                    </div>

                    {/* Review text */}
                    <div className="mb-6 flex-grow">
                      <PrismicRichText
                        field={logoReviewItem.review}
                        components={{
                          paragraph: ({ children }) => (
                            <p
                              style={{
                                color: '#D7EDE3',
                                fontFamily: 'Inter',
                                fontSize: '16px',
                                fontWeight: 400,
                                lineHeight: '150%',
                              }}
                            >
                              {children}
                            </p>
                          ),
                        }}
                      />
                    </div>

                    {/* Author info */}
                    <div className="mt-auto">
                      <p className="text-white font-medium">
                        {logoReviewItem.author}
                      </p>
                      <p className="text-white/70 text-sm">
                        {logoReviewItem.authors_title}
                      </p>
                    </div>
                  </div>
                </div>
              );
            }

            if (isWithoutBgp) {
              return (
                <div key={index} className="px-2 h-full">
                  <div className="glassy-container rounded-2xl p-6 flex flex-col justify-between text-left h-full">
                    <div>
                      {item.card_title && (
                        <PrismicRichText
                          field={item.card_title}
                          components={{
                            heading3: ({ children }) => (
                              <h3 className="text-xl font-semibold mb-4">
                                {children}
                              </h3>
                            ),
                          }}
                        />
                      )}
                      <div className="flex items-center gap-4 mb-4">
                        {item.metric_trend ? (
                          <ArrowUpRightIcon className="w-8 h-8 text-green-400" />
                        ) : (
                          <ArrowDownRightIcon className="w-8 h-8 text-red-400" />
                        )}
                        <div className="text-5xl font-bold">
                          <PrismicRichText field={item.card_metric} />
                        </div>
                        <div className="text-white/70 max-w-[100px]">
                          <PrismicRichText
                            field={item.card_metric_description}
                          />
                        </div>
                      </div>
                      <PrismicNextLink
                        field={item.card_cta}
                        className="text-green-400 hover:underline"
                      >
                        View details
                      </PrismicNextLink>
                    </div>
                    <div className="mt-8 pt-6 border-t border-white/10">
                      <PrismicNextImage
                        field={item.card_image}
                        className="h-8 object-contain self-start"
                        fallbackAlt=""
                      />
                    </div>
                  </div>
                </div>
              );
            }

            return (
              <div key={index} className="px-2">
                <div className="bg-white/5 border border-white/10 rounded-lg overflow-hidden backdrop-blur-sm">
                  <div className="p-6">
                    <div className="font-bold text-xl mb-4">
                      <PrismicRichText field={item.card_title} />
                    </div>
                    <div className="flex items-center text-3xl font-bold mb-4">
                      {item.metric_trend ? (
                        <ArrowUp size={28} className="text-green-400 mr-2" />
                      ) : (
                        <ArrowDown size={28} className="text-red-400 mr-2" />
                      )}
                      <PrismicRichText field={item.card_metric} />
                      <span className="text-lg font-normal ml-2">
                        <PrismicRichText field={item.card_metric_description} />
                      </span>
                    </div>
                    <PrismicNextLink
                      field={item.card_cta}
                      className="text-green-400 font-semibold hover:underline"
                    >
                      View details
                    </PrismicNextLink>
                  </div>
                  <PrismicNextImage
                    field={item.card_image}
                    className="w-full h-48 object-cover"
                    fallbackAlt=""
                  />
                </div>
              </div>
            );
          })}
        </Slider>
      </div>
    </section>
  );
};

export default CaseStudiesCarousel;
